import cx from 'clsx';
import { forwardRef, type ReactElement, type ReactNode, useReducer } from 'react';
import { useObjectRef } from 'react-aria';
import { useCopyToClipboard } from 'usehooks-ts';
import IconCopyOutlined from '@repo/monochrome-icons/icon-copy-outlined';
import {
  Button as AriaButton,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult,
} from 'react-aria-components';
import { useIntl } from 'react-intl';
import { Description } from '#components/form-elements/description/description';
import { ErrorMessage } from '#components/form-elements/error-message/error-message';
import { Input } from '#components/form-elements/input/input';
import { FieldLabel } from '#components/form-elements/label/label';
import { useToast } from '#components/toast/provider/use-toast';
import { Tooltip } from '#components/tooltip/tooltip';
import { EyeIcon } from './icons/eye';
import { EyeHiddenIcon } from './icons/eye-hidden';
import styles from './text-field.module.css';

interface TextFieldBaseProps extends AriaTextFieldProps {
  description?: string | ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string) | ReactNode;
  placeholder: string;
  multiple?: boolean;
  isOptional?: boolean;
  enableCopy?: boolean;
  onCopySuccessMessage?: string;
  onCopySuccess?: () => void;
}

// Omitting the visible `label` requires `aria-label`/`aria-labelledby` so the
// input always keeps a programmatic accessible name (WCAG 4.1.2).
type TextFieldLabelProps =
  | { label: string | ReactNode }
  | { label?: undefined; 'aria-label': string }
  | { label?: undefined; 'aria-labelledby': string };

export type TextFieldComponentProps = TextFieldBaseProps & TextFieldLabelProps;

export const TextField = forwardRef<HTMLInputElement, TextFieldComponentProps>(function TextField(
  {
    label,
    description,
    errorMessage,
    placeholder,
    className,
    type,
    multiple,
    isOptional,
    enableCopy = false,
    onCopySuccessMessage,
    onCopySuccess,
    ...props
  },
  ref
): ReactElement {
  const { formatMessage } = useIntl();
  const { showToast } = useToast();
  const [isPasswordVisible, togglePasswordVisibility] = useReducer(state => !state, false);
  const inputRef = useObjectRef(ref);
  const [_copiedText, copy] = useCopyToClipboard();

  const copyLabel = formatMessage({ id: 'a11y-buttons-copy-aria-label' });

  const handleCopy = async (): Promise<void> => {
    if (await copy(inputRef.current?.value ?? '')) {
      showToast({
        text:
          onCopySuccessMessage ??
          formatMessage({ id: 'design-system-kit.text-field.copied-toast' }),
        type: 'info',
      });
      onCopySuccess?.();
    }
  };

  const resolvedErrorMessage =
    errorMessage ??
    (props.isRequired
      ? ({ validationDetails }: ValidationResult) =>
          validationDetails.valueMissing ? formatMessage({ id: 'field-error-required' }) : ''
      : undefined);

  return (
    <AriaTextField
      className={cx(className, styles.container)}
      type={!isPasswordVisible ? type : 'text'}
      {...props}
      isReadOnly={enableCopy || props.isReadOnly}
    >
      {({ isInvalid }) => (
        <>
          {Boolean(label) && <FieldLabel isOptional={isOptional}>{label}</FieldLabel>}
          <span className={styles.inputContainer}>
            <Input
              autoFocus={props.autoFocus}
              multiple={multiple}
              placeholder={placeholder}
              ref={inputRef}
            />

            {enableCopy ? (
              <Tooltip closeDelay={0} delay={300} label={copyLabel} placement="top">
                {/* react-aria tooltips only describe their trigger, so the icon-only button still needs a name. */}
                <AriaButton
                  aria-label={copyLabel}
                  className={styles.icon}
                  data-copy
                  onPress={() => void handleCopy()}
                >
                  <IconCopyOutlined />
                </AriaButton>
              </Tooltip>
            ) : (
              type === 'password' && (
                <button
                  aria-label={
                    isPasswordVisible
                      ? formatMessage({ id: 'a11y-buttons-hide-password-aria-label' })
                      : formatMessage({ id: 'a11y-buttons-show-password-aria-label' })
                  }
                  className={styles.icon}
                  onClick={togglePasswordVisibility}
                  type="button"
                >
                  {isPasswordVisible ? <EyeHiddenIcon /> : <EyeIcon />}
                </button>
              )
            )}
          </span>
          <ErrorMessage isInvalid={isInvalid}>{resolvedErrorMessage}</ErrorMessage>
          {Boolean(description) && <Description>{description}</Description>}
        </>
      )}
    </AriaTextField>
  );
});
